import {
  markersCoverSpan,
  markersOneSpeaker,
  markersSeed,
  previewSpanText,
  previousKnobValues,
  previousNote,
  repeatsPreviousDirection,
} from '@src/services/dubbing/directionPrefill';
import {
  defaultKnobValues,
  knobValuesOutOfRange,
  settingsDirectionFrom,
} from '@src/services/dubbing/directionSupport';
import { useDatoClient, useDubbingStore } from '@src/stores-v2/StoreContext';
import { getSpeakerNameFromLabel } from '@src/utility/speakerUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDubDirectionCapability,
  useDubDirectionPrefill,
  useDubSettingsPanelEnabled,
  useDubSpanSpeakers,
} from './useDubDirectionCapability';

import type {
  AutoTagResult,
  DubDirectionFieldProps,
} from './DubDirectionField';
import type { DubDirectionCapabilityRead } from './useDubDirectionCapability';
import type { DubDirection } from '@src/repositories/DubbingRepository';
import type { DirectionPreview } from '@src/services/dubbing/directionPreview';
import type {
  DirectionKnob,
  DirectionVoice,
} from '@src/services/dubbing/directionSupport';

/**
 * PLAT-12022 — the direction block of a dub run, as state: the written note
 * (PLAT-11384), the voice-settings panel (PLAT-11797), the delivery-markers
 * editor (PLAT-11798), the capability read that decides which of those is
 * offered (PLAT-11385), the pre-fill from the last run (PLAT-11383), and the
 * ONE direction the run will carry.
 *
 * Lifted out of `DubGenerateModal` so the whole-story dub popover and the
 * selection re-dub popover (`RedubSelectionPopover`) ask the same questions
 * and send the same thing. A host gives it the run's voices as it resolved
 * them and spreads `fieldProps` into `<DubDirectionField>`; it reads
 * `direction` when it starts the run and `blocked` to hold the button.
 *
 * It imports no store writes: it reads the voice catalog to name each voice's
 * model, and the dub manifest (through the capability hooks) to learn which
 * speakers a section covers and what the last run asked for.
 */

// One stable empty list, so the capability read is not re-asked every render
// while the run's voices are still being resolved.
const NO_DIRECTION_VOICES: DirectionVoice[] = [];
// What the direction block is told while an earlier read it depends on (which
// speakers a selected section covers) is still out.
const DIRECTION_READ_PENDING: DubDirectionCapabilityRead = {
  settled: false,
  support: null,
  failed: false,
};
// One stable empty list, so the panel's seeding effect does not re-run on every
// render while the models are still answering.
const EMPTY_KNOBS: DirectionKnob[] = [];

export interface DubDirectionStateParams {
  storyId: string;
  /** The run's target language (BCP-47). */
  language: string;
  /**
   * The run's line ids; absent for a whole-language run. Decides where the
   * controls START (the last direction on those lines) and whether the markers
   * editor can be offered (up to `MAX_MARKED_LINES` lines, PLAT-12198).
   */
  segmentIds?: readonly string[];
  /**
   * PLAT-11373 — true for a section re-dub: the capability read is then
   * narrowed to the speakers whose lines the section covers, read from the
   * stored dub, instead of every voice in the run.
   */
  scopeToSpanSpeakers: boolean;
  /** Transcript label → DatoCMS speaker id, as the host resolved it. */
  labelToDatoId: Readonly<Record<string, string>>;
  /** DatoCMS speaker id → the AI Voice the run will use for them. */
  selections: Readonly<Record<string, string>>;
  /** The run's speakers, for naming a voice's owner in the hint. */
  speakers: readonly { id: string; name: string }[];
  /**
   * PLAT-12080 — bumped by a host that keeps the controls on screen across
   * a landed take, so the prefill (and with it the markers, the knobs and
   * the note) starts again from the take that just landed.
   */
  reloadToken?: number;
}

export interface DubDirectionState {
  /** Everything `DubDirectionField` takes, minus `disabled` (the host's call). */
  fieldProps: Omit<DubDirectionFieldProps, 'disabled'>;
  /** The one direction this run will carry, or none. */
  direction: DubDirection | undefined;
  /**
   * PLAT-11797 — a control is set outside what the models declared. The run
   * is held rather than sent to be refused.
   */
  blocked: boolean;
  /**
   * PLAT-12023 — what a speaker's play button previews: their own words in
   * the run's span, with the direction the run would send right now, and how
   * their voice's model can be directed. `undefined` for a speaker the run
   * has no voice for. Hosts pass it to their speaker rows.
   */
  previewFor: (speakerId: string) => DirectionPreview | undefined;
}

export function useDubDirectionState(
  params: DubDirectionStateParams,
): DubDirectionState {
  const {
    storyId,
    language,
    segmentIds,
    scopeToSpanSpeakers,
    labelToDatoId,
    selections,
    speakers,
  } = params;
  const dubbingStore = useDubbingStore();
  const datoClient = useDatoClient();
  const isScoped = !!segmentIds?.length;

  // The free-form direction for this run (PLAT-11384): how the lines should
  // be delivered, in the director's own words.
  //
  // It OPENS holding the last run's note (PLAT-11383), like the other two
  // forms. That is not the note being carried forward by itself: a direction
  // states the delivery wanted, complete, and replaces the one before it, so
  // a person is shown what they are replacing. Clear the field and the run is
  // undirected, exactly as before.
  const [directionNote, setDirectionNote] = useState('');
  // PLAT-11797 — where each of the model's own numeric and on/off controls is
  // set right now. What is SENT is decided against the models' own declared
  // values, not against whatever the panel opened at, because the panel opens
  // at the previous run's values and a person who leaves those on screen is
  // asking for them.
  const [knobValues, setKnobValues] = useState<
    Record<string, number | boolean>
  >({});
  // PLAT-11798 / PLAT-12198 — each line's own words with delivery markers
  // written into them, in spoken order. `null` until the lines' words have
  // been read; the editor is not offered before that.
  const [markersLines, setMarkersLines] = useState<string[] | null>(null);

  // PLAT-11373 — which speakers a section re-dub's own lines are spoken by,
  // read from the stored dub manifest. The mapper shows every speaker in the
  // story even in this mode, so without this a section spoken by one person
  // would be judged against voices that have no line in it. `null` means
  // "not known / not scoped", and the whole run's voices are used.
  const spanSpeakers = useDubSpanSpeakers({
    storyId,
    language,
    segmentIds: scopeToSpanSpeakers ? segmentIds : undefined,
  });

  // PLAT-11385 — the voices this run will actually speak with, each with the
  // text-to-speech model behind it, so the popover can say how they can be
  // directed before anything is typed.
  const directionVoices = useMemo<DirectionVoice[]>(() => {
    if (!spanSpeakers.settled) return NO_DIRECTION_VOICES;
    const inScope = spanSpeakers.labels;
    const nameById = new Map(speakers.map(s => [s.id, s.name]));
    const voices: DirectionVoice[] = [];
    // One person can carry two labels; they are one voice, listed once.
    const seen = new Set<string>();
    for (const [label, datoId] of Object.entries(labelToDatoId)) {
      if (inScope && !inScope.includes(label)) continue;
      if (seen.has(datoId)) continue;
      seen.add(datoId);
      const voiceId = selections[datoId];
      if (!voiceId) continue;
      const record = dubbingStore.voiceById(voiceId);
      voices.push({
        speakerName: nameById.get(datoId) ?? getSpeakerNameFromLabel(label),
        voiceId,
        ...(record?.model?.modelId ? { modelId: record.model.modelId } : {}),
        ...(record?.model?.displayName
          ? { modelLabel: record.model.displayName }
          : {}),
      });
    }
    return voices;
    // `catalog` is in the list because the voice records arrive after this
    // popover mounts; without it the memo would keep the pre-catalog answer
    // ("no model named") and never offer a direction.
  }, [
    spanSpeakers.settled,
    spanSpeakers.labels,
    speakers,
    labelToDatoId,
    selections,
    dubbingStore,
    dubbingStore.catalog,
  ]);
  const capabilityRead = useDubDirectionCapability(directionVoices);
  const directionRead = spanSpeakers.settled
    ? capabilityRead
    : DIRECTION_READ_PENDING;
  // The one gate on sending a direction: every voice in this run declares it
  // takes a written one. False while the models are still answering, so a
  // run started in that window is undirected rather than refused.
  const noteAccepted = directionRead.support?.noteAccepted === true;

  // PLAT-11797 — is the numeric / on-off controls panel switched on for this
  // person? A LaunchDarkly switch, off by default.
  const settingsPanelEnabled = useDubSettingsPanelEnabled();

  // PLAT-11383 / PLAT-11798 — where the controls should start: the last run's
  // direction, and the words of the one line a markers editor covers. This
  // read never hides a control; it only decides starting values, so the
  // direction block does not wait on it.
  const prefill = useDubDirectionPrefill({
    storyId,
    language,
    ...(isScoped && segmentIds ? { segmentIds } : {}),
    ...(params.reloadToken !== undefined
      ? { reloadToken: params.reloadToken }
      : {}),
  });

  const knobs = directionRead.support?.knobs ?? EMPTY_KNOBS;
  // The panel's starting position: the previous run's values where it set
  // them, and the model's own declared value for every control it did not.
  // A direction states the complete delivery wanted and replaces the one
  // before it, and a person can only meaningfully replace what they can see
  // (settled 2026-09-20).
  const knobStartValues = useMemo(() => {
    const fromModel = defaultKnobValues(knobs);
    const fromLastRun = previousKnobValues(prefill.previous);
    if (!fromLastRun) return fromModel;
    const merged = { ...fromModel };
    for (const knob of knobs) {
      const value = fromLastRun[knob.name];
      if (value !== undefined && typeof value === knob.type) {
        merged[knob.name] = value;
      }
    }
    return merged;
  }, [knobs, prefill.previous]);

  // Seed the panel once the controls and the previous run are both known, and
  // re-seed if either changes (a speaker's voice can change under the
  // popover). Anything already typed by hand is replaced, because the panel
  // is showing a different set of controls than the one that was typed into.
  const knobSeedKey = `${knobs.map(k => k.name).join(',')}|${JSON.stringify(
    knobStartValues,
  )}`;
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededKeyRef.current === knobSeedKey) return;
    seededKeyRef.current = knobSeedKey;
    setKnobValues(knobStartValues);
  }, [knobSeedKey, knobStartValues]);

  // PLAT-11383 — the written note starts from the last run's note. There is
  // no "model default" for prose, so with no previous note it starts empty.
  // Seeded once, and again only if the previous note itself changes, so a
  // note being typed is never wiped by an unrelated re-render.
  const seededNoteRef = useRef<string | null>(null);
  useEffect(() => {
    if (!prefill.settled) return;
    const last = previousNote(prefill.previous);
    if (last === null || seededNoteRef.current === last) return;
    seededNoteRef.current = last;
    setDirectionNote(last);
  }, [prefill.settled, prefill.previous]);

  // PLAT-11798 — the markers editor starts from the last run's MARKED-UP
  // text, so that removing a marker removes it. Falls back to the line's own
  // words when there is none to restore, or when the stored markup is over
  // words this line no longer has. PLAT-12198 — each line seeds from the
  // markup it was itself last recorded with.
  const markers = useMemo(() => {
    if (!prefill.lines) {
      return { texts: null, plain: null, staleMarkersDropped: false };
    }
    const seeds = prefill.lines.map(line =>
      markersSeed({
        previous: line.previous,
        lineText: line.text,
        lineId: line.segmentId,
      }),
    );
    return {
      texts: seeds.map(seed => seed.text ?? ''),
      plain: prefill.lines.map(line => line.text),
      staleMarkersDropped: seeds.some(seed => seed.staleMarkersDropped),
    };
  }, [prefill.lines]);
  const seededKey = markers.texts ? JSON.stringify(markers.texts) : null;
  const seededLinesRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededKey === null || !markers.texts) return;
    if (seededLinesRef.current === seededKey) return;
    seededLinesRef.current = seededKey;
    setMarkersLines(markers.texts);
  }, [seededKey, markers.texts]);

  // One speaker's lines only: a span across a speaker change is refused like
  // a span too long, with the same note (PLAT-12198).
  const markersSpan =
    markersCoverSpan(isScoped ? segmentIds : undefined) &&
    (prefill.lines === null ||
      markersOneSpeaker(prefill.lines, label => labelToDatoId[label]));
  const markersOffered =
    directionRead.support?.tagsAccepted === true &&
    markersSpan &&
    markers.texts !== null;
  const panelOffered =
    settingsPanelEnabled &&
    directionRead.support?.settingsAccepted === true &&
    knobs.length > 0;

  // PLAT-11797 — a value outside what the models declared can be typed into a
  // number field, so it is caught here and the run is held rather than sent
  // to be refused. Only counted when the panel is actually on screen.
  const knobsOutOfRange = useMemo(
    () => (panelOffered ? knobValuesOutOfRange(knobs, knobValues) : []),
    [panelOffered, knobs, knobValues],
  );

  /**
   * The one direction this run will carry, or none.
   *
   * A run carries at most ONE direction, so where a voice takes more than one
   * form the most specific thing the person actually did wins: markers
   * written into the line, then the controls panel moved off where it
   * started, then the written note. Each is only ever considered when it is
   * on screen.
   */
  const direction = useMemo<DubDirection | undefined>(() => {
    if (markersOffered && markersLines && prefill.lines) {
      // Untouched words are not a direction: sending a line back exactly as
      // it was voiced asks for nothing. Only the lines that changed are sent.
      const edited = prefill.lines.flatMap((line, i) => {
        const text = markersLines[i] ?? '';
        return text.trim() && text.trim() !== line.text.trim()
          ? [{ segmentId: line.segmentId, text }]
          : [];
      });
      // One line keeps the single-line shape every reader already knows;
      // several are keyed by line (PLAT-12198).
      if (edited.length > 0 && prefill.lines.length === 1) {
        return { kind: 'tags', taggedText: edited[0].text };
      }
      if (edited.length > 0) {
        return {
          kind: 'tags-by-line',
          taggedTextByLine: Object.fromEntries(
            edited.map(line => [line.segmentId, line.text]),
          ),
        };
      }
    }
    if (panelOffered) {
      const settings = settingsDirectionFrom(knobs, knobValues);
      if (settings) return settings;
    }
    const note = directionNote.trim();
    return note && noteAccepted ? { kind: 'free-form', note } : undefined;
  }, [
    markersOffered,
    markersLines,
    prefill.lines,
    panelOffered,
    knobs,
    knobValues,
    directionNote,
    noteAccepted,
  ]);

  // PLAT-11383 — about to ask for the very same delivery as last time.
  const repeatsPrevious = repeatsPreviousDirection(direction, prefill.previous);

  const handleKnobChange = useCallback(
    (name: string, next: number | boolean) =>
      setKnobValues(prev => ({ ...prev, [name]: next })),
    [],
  );

  // PLAT-12023 / PLAT-12029 — Auto-tag writes the markers into the lines for
  // the person, from the model's documented vocabulary and the note. Offered
  // only where the markers editor is, and only when the run's one voice
  // names its model (the vocabulary is the model's); absent, the button is
  // absent too. The route tags one line per call, so a passage is one call
  // per line (PLAT-12198); a failed call keeps its line as it was, and the
  // repository has already logged it.
  const autoTagModelId =
    markersOffered && directionVoices.length === 1
      ? directionVoices[0]?.modelId
      : undefined;
  const handleAutoTag = useMemo(
    () =>
      autoTagModelId
        ? async (): Promise<AutoTagResult> => {
            const note = directionNote.trim();
            const lines = markersLines ?? markers.plain ?? [];
            const results = await Promise.allSettled(
              lines.map(text =>
                datoClient.dubbingRepository.autoTag({
                  text,
                  modelId: autoTagModelId,
                  ...(note ? { note } : {}),
                }),
              ),
            );
            return {
              lines: results.map((result, i) =>
                result.status === 'fulfilled'
                  ? result.value.taggedText
                  : lines[i],
              ),
              failed: results.flatMap((result, line) =>
                result.status === 'rejected'
                  ? [{ line, error: result.reason }]
                  : [],
              ),
            };
          }
        : undefined,
    [autoTagModelId, datoClient, markersLines, markers.plain, directionNote],
  );

  // PLAT-12198 — a preview speaks ONE text, so markup keyed by line is
  // previewed as the passage it reads as: every line, in spoken order, joined
  // the way the preview joins the speaker's words.
  const previewDirection = useMemo<DubDirection | undefined>(
    () =>
      direction?.kind === 'tags-by-line' && markersLines
        ? { kind: 'tags', taggedText: markersLines.join(' ') }
        : direction,
    [direction, markersLines],
  );

  // PLAT-12023 — what each speaker's play button previews. Their words come
  // from the stored dub (this run's span, or the whole dub for a
  // whole-language run); how their model can be directed comes from the
  // capability read; the direction is the one the run would send now.
  const labelsByDatoId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [label, datoId] of Object.entries(labelToDatoId)) {
      map.set(datoId, [...(map.get(datoId) ?? []), label]);
    }
    return map;
  }, [labelToDatoId]);
  const previewFor = useCallback(
    (speakerId: string): DirectionPreview | undefined => {
      const voiceId = selections[speakerId];
      if (!voiceId) return undefined;
      const speakerName =
        speakers.find(s => s.id === speakerId)?.name ??
        getSpeakerNameFromLabel(labelsByDatoId.get(speakerId)?.[0] ?? '');
      const summary = directionRead.support?.voices.find(
        v => v.speakerName === speakerName,
      );
      const text = previewSpanText(
        prefill.manifest,
        isScoped ? segmentIds : undefined,
        labelsByDatoId.get(speakerId) ?? [],
      );
      return {
        direction: previewDirection,
        text,
        mechanisms: summary?.mechanisms ?? [],
        speakerName,
      };
    },
    [
      selections,
      speakers,
      labelsByDatoId,
      directionRead.support,
      prefill.manifest,
      isScoped,
      segmentIds,
      previewDirection,
    ],
  );

  const fieldProps = useMemo<Omit<DubDirectionFieldProps, 'disabled'>>(
    () => ({
      value: directionNote,
      onChange: setDirectionNote,
      read: directionRead,
      settingsPanelEnabled,
      knobValues,
      onKnobChange: handleKnobChange,
      knobsOutOfRange,
      markersCoverSpan: markersSpan,
      markersLines: markers.texts === null ? null : markersLines,
      onMarkersChange: setMarkersLines,
      markersStaleDropped: markers.staleMarkersDropped,
      ...(handleAutoTag ? { onAutoTag: handleAutoTag } : {}),
      repeatsPrevious,
    }),
    [
      directionNote,
      directionRead,
      settingsPanelEnabled,
      knobValues,
      handleKnobChange,
      knobsOutOfRange,
      markersSpan,
      markers.texts,
      markers.staleMarkersDropped,
      markersLines,
      handleAutoTag,
      repeatsPrevious,
    ],
  );

  return {
    fieldProps,
    direction,
    blocked: knobsOutOfRange.length > 0,
    previewFor,
  };
}
